import { useCallback, useMemo } from "react";
import { format, isPast, isSameDay, isToday } from "date-fns";
import { XIcon } from "lucide-react";

import {
  useAppProperties,
  useCollections,
  useUserEvents,
  useVideos,
} from "@/hooks";
import { ItemType, UserEvent } from "@/api";
import {
  EVENT_TYPE_SCHEDULED,
  EVENT_TYPE_WATCHED,
  EVENT_TYPE_JOURNAL,
  ITEM_TYPE_COLLECTION,
  ITEM_TYPE_VIDEO,
} from "@/const.ts";
import { cn, getContentThumbnailUrl, getDateFromLocalString } from "@/utils";
import { Check, Clock } from "@/icons";
import { ContentThumbnail } from "@/components";
import { isClientScheduledEnabled } from "@/utils";

import { IconWrapper } from "../components/IconWrapper.tsx";
import { useClientCalendar } from "./useClientCalendar.tsx";
import { CalendarEvent } from "../util.tsx";

interface IconOptions {
  size: string;
  colorOnly?: boolean;
}

export const useCalendarData = () => {
  const { data } = useUserEvents();
  const { data: clientCalendarData } = useClientCalendar();
  const { data: properties } = useAppProperties();

  const clientData = clientCalendarData?.dates;

  const eventByDate = useMemo(() => {
    if (!data) return {};
    const userEvents = data.Events.reduce(
      (acc, event) => {
        const [year, month, day] = event.EventDate.split(" ")[0].split("-");
        const paddedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        if (!acc[paddedDate]) {
          acc[paddedDate] = [];
        }
        acc[paddedDate].push(event);
        return acc;
      },
      {} as Record<string, CalendarEvent[]>,
    );
    // Merge client calendar data
    if (isClientScheduledEnabled()) {
      Object.keys(clientData ?? {}).forEach((yearMonth) => {
        if (clientData) {
          Object.keys(clientData[yearMonth]).forEach((day) => {
            const items = clientData[yearMonth][day].Items ?? [];
            const paddedDate = `${yearMonth}-${day.padStart(2, "0")}`;

            if (!userEvents[paddedDate]) {
              userEvents[paddedDate] = [];
            }

            userEvents[paddedDate].push(
              ...items.map((i) => {
                return {
                  EventDate: `${paddedDate} 12:00:00`,
                  ItemId: i.Id.toString(),
                  EventType: "scheduled",
                  ItemType: i.Type,
                  DataSource: "",
                  SourceId: "",
                  EventData: "",
                  isClientScheduled: true,
                };
              }),
            );
          });
        }
      });
    }

    return userEvents;
  }, [clientData, data]);

  const journalPrompts = useMemo(() => {
    if (properties?.ShowJournal === "0") {
      return [];
    }

    return Object.entries(clientCalendarData?.journalPrompts ?? {});
  }, [clientCalendarData, properties]);

  // Determine which Videos and Collections to lookup in Content API
  const { collectionIds, videoIds } = useMemo(() => {
    const cols = new Set<number>();
    const vids = new Set<number>();
    const items: { ChildId: string; Type: ItemType }[] = [];
    const events = data?.Events ?? [];

    // Process user events
    for (const e of events) {
      if (e.ItemType === ITEM_TYPE_VIDEO) {
        vids.add(parseInt(e.ItemId));
      }
      if (e.ItemType === ITEM_TYPE_COLLECTION) {
        cols.add(parseInt(e.ItemId));
      }
    }

    // Process clientData Items if client scheduling is enabled
    if (isClientScheduledEnabled() && clientData) {
      Object.keys(clientData).forEach((yearMonth) => {
        Object.values(clientData[yearMonth]).forEach((day) => {
          const items = day.Items ?? [];
          for (const i of items) {
            if (i.Type === ITEM_TYPE_VIDEO) {
              vids.add(i.Id); // Add clientData video IDs
            }
            if (i.Type === ITEM_TYPE_COLLECTION) {
              cols.add(i.Id); // Add clientData collection IDs
            }
          }
        });
      });
    }

    return {
      collectionIds: Array.from(cols),
      videoIds: Array.from(vids),
      items,
    };
  }, [clientData, data?.Events]);
  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });

  const getEventItem = useCallback(
    (e: UserEvent) => {
      const id = parseInt(e.ItemId);
      if (e.ItemType === ITEM_TYPE_COLLECTION && collectionById) {
        const collection = collectionById[id];
        if (collection) {
          return {
            title: collection.Name,
            content: collection,
          };
        }
      }

      if (e.ItemType === ITEM_TYPE_VIDEO && videoById) {
        const video = videoById[id];
        if (video) {
          return {
            title: video.Title,
            content: video,
          };
        }
      }
      return undefined;
    },
    [collectionById, videoById],
  );

  const getEventTitle = useCallback(
    (e: UserEvent) => {
      const item = getEventItem(e);
      return item?.title ?? "";
    },
    [getEventItem],
  );

  const getEventsForDate = useCallback(
    (d: Date) => {
      const currentDate = new Date();
      const formattedDate = format(d, "yyyy-MM-dd");
      const events = eventByDate[formattedDate] || [];
      return events
        .filter((e) => {
          if (!getEventItem(e)) {
            return false;
          }
          if (e.EventType === EVENT_TYPE_SCHEDULED) {
            if (isSameDay(d, currentDate) || isPast(d)) {
              // If its been completed already just show the completed event
              return !events.some(
                (_e) =>
                  _e.EventType === EVENT_TYPE_WATCHED && e.ItemId === _e.ItemId,
              );
            }
          }
          return true;
        })
        .sort((a, b) => a.EventDate.localeCompare(b.EventDate));
    },
    [eventByDate, getEventItem],
  );

  const getJournalEntriesForDate = useCallback(
    (d: Date) => {
      if (journalPrompts.length === 0) {
        return [];
      }

      const formattedDate = format(d, "yyyy-MM-dd");
      const events = eventByDate[formattedDate] || [];
      return events.filter(({ EventType }) => EventType === EVENT_TYPE_JOURNAL);
    },
    [eventByDate, journalPrompts],
  );

  const getEventIcon = useCallback(
    (e: CalendarEvent, options?: IconOptions) => {
      const additionalClasses = [];
      if (options?.size === "large") {
        additionalClasses.push("h-4 w-4");
      } else if (options?.size === "small") {
        additionalClasses.push("h-[4px] w-[4px]");
      }

      // Watched events are always green ticks
      if (e.EventType === EVENT_TYPE_WATCHED) {
        return (
          <IconWrapper className={cn("bg-watchedColor", additionalClasses)}>
            {!options?.colorOnly && <Check width="100%" height="100%" />}
          </IconWrapper>
        );
      }

      if (e.EventType === EVENT_TYPE_SCHEDULED) {
        const date = getDateFromLocalString(e.EventDate);
        // Missed is only if a past date and not client scheduled
        if (date && isPast(date) && !e.isClientScheduled && !isToday(date)) {
          return (
            <IconWrapper className={cn("bg-missedColor", additionalClasses)}>
              {!options?.colorOnly && <XIcon width="100%" height="100%" />}
            </IconWrapper>
          );
        }
        return (
          <IconWrapper className={cn("bg-scheduledColor", additionalClasses)}>
            {!options?.colorOnly && <Clock width="100%" height="100%" />}
          </IconWrapper>
        );
      }
    },
    [],
  );

  const getEventThumbnail = useCallback(
    (e: UserEvent) => {
      const item = getEventItem(e);

      if (!item) {
        return undefined;
      }

      const { content } = item;
      return (
        <ContentThumbnail
          thumbnail={getContentThumbnailUrl(content)}
          contentItem={content}
          className="rounded-lg"
        />
      );
    },
    [getEventItem],
  );

  const getEventTime = useCallback((e: UserEvent) => {
    const date = getDateFromLocalString(e.EventDate);
    if (!date) {
      return null; // Bad Event Date
    }
    return format(date, "h:mmaaa").toUpperCase();
  }, []);

  const isLoading = useMemo(() => {
    let loading = false;
    if (data === undefined) {
      loading = true;
    } else if (videoIds && videoIds?.length > 0 && !videoById) {
      loading = true;
    } else if (collectionIds && collectionIds.length > 0 && !collectionById) {
      loading = true;
    }
    return loading;
  }, [data, videoById, videoIds, collectionById, collectionIds]);

  return {
    getEventsForDate,
    getJournalEntriesForDate,
    getEventTitle,
    getEventIcon,
    getEventThumbnail,
    getEventTime,
    journalPrompts,
    videoById,
    collectionById,
    isLoading,
  };
};
